import { Building2, User, Mail, Phone, Globe } from 'lucide-react';
import { Badge } from './Badge';
import { SentimentIndicator } from '../modules/SentimentIndicator';
import { Skeleton } from './Skeleton';

interface EntityCardProps {
  data: any;
  type: 'contact' | 'company';
  loading?: boolean;
}

export function EntityCard({ data, type, loading }: EntityCardProps) {
  if (loading) {
    return (
      <div className="w-72 p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-1.5 flex-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }

  if (!data) return null;

  if (type === 'contact') {
    return (
      <div className="w-72 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-nexus-700 flex items-center justify-center text-sm font-bold text-nexus-200">
            {data.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-surface-100 truncate">{data.name}</p>
            <div className="flex items-center gap-2">
              <Badge size="sm">{data.type || 'contact'}</Badge>
              {data.sentiment && <SentimentIndicator sentiment={data.sentiment} size="sm" />}
            </div>
          </div>
        </div>
        {data.email && (
          <div className="flex items-center gap-2 text-xs text-surface-400 mb-1">
            <Mail className="h-3 w-3" /> {data.email}
          </div>
        )}
        {data.phone && (
          <div className="flex items-center gap-2 text-xs text-surface-400 mb-1">
            <Phone className="h-3 w-3" /> {data.phone}
          </div>
        )}
        {(data.leadScore != null || data.churnRisk != null) && (
          <div className="flex gap-4 mt-3 pt-3 border-t border-surface-700">
            {data.leadScore != null && (
              <div>
                <p className="text-[10px] text-surface-500 uppercase">Lead Score</p>
                <p className="text-sm font-semibold text-surface-200">{data.leadScore}%</p>
              </div>
            )}
            {data.churnRisk != null && (
              <div>
                <p className="text-[10px] text-surface-500 uppercase">Churn Risk</p>
                <p className="text-sm font-semibold text-surface-200">{data.churnRisk}%</p>
              </div>
            )}
            {data.totalInteractions != null && (
              <div>
                <p className="text-[10px] text-surface-500 uppercase">Interactions</p>
                <p className="text-sm font-semibold text-surface-200">{data.totalInteractions}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Company card
  return (
    <div className="w-72 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-surface-100 truncate">{data.name}</p>
          {data.industry && <p className="text-xs text-surface-400">{data.industry}</p>}
        </div>
      </div>
      {data.domain && (
        <div className="flex items-center gap-2 text-xs text-surface-400 mb-1">
          <Globe className="h-3 w-3" /> {data.domain}
        </div>
      )}
      {data.size && (
        <div className="flex items-center gap-2 text-xs text-surface-400">
          <User className="h-3 w-3" /> {data.size}
        </div>
      )}
    </div>
  );
}
